import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { patternAPI, accountAPI } from '../services/api';

// ── 공통 패턴 테이블 컴포넌트 ──────────────────────────────
export type TradePattern = {
  login: number;
  trade_count: number;
  win_rate: number;
  avg_profit: number;
  total_profit: number;
  avg_hold_hours: number;
  avg_win: number;
  avg_loss: number;
  payoff_ratio?: number;
  first_trade: string;
  last_trade: string;
  balance?: number;
  equity?: number;
};

const formatMoney = (v?: number) =>
  v == null ? '-' : `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatHours = (h: number) => {
  if (h < 1) return `${Math.round(h * 60)}분`;
  if (h < 24) return `${h.toFixed(1)}시간`;
  return `${(h / 24).toFixed(1)}일`;
};

export const PatternTable: React.FC<{
  data: TradePattern[];
  loading: boolean;
  showPayoff?: boolean;
  registeredAccounts: Set<string>;
  onRegister: (login: number) => void;
  registeringLogin: number | null;
  badgeColor: string;
  badgeLabel: string;
}> = ({ data, loading, showPayoff, registeredAccounts, onRegister, registeringLogin, badgeColor, badgeLabel }) => {
  if (loading) return <div style={styles.loading}>분석 중... (시간이 걸릴 수 있어요)</div>;

  return (
    <div style={styles.tableWrapper}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>계좌번호</th>
            <th style={styles.th}>거래수</th>
            <th style={styles.th}>승률</th>
            <th style={styles.th}>평균수익</th>
            <th style={styles.th}>총손익</th>
            <th style={styles.th}>평균보유</th>
            <th style={styles.th}>평균Win</th>
            <th style={styles.th}>평균Loss</th>
            {showPayoff && <th style={styles.th}>Payoff</th>}
            <th style={styles.th}>첫거래</th>
            <th style={styles.th}>마지막거래</th>
            <th style={styles.th}>액션</th>
          </tr>
        </thead>
        <tbody>
          {data.map(r => {
            const isRegistered = registeredAccounts.has(String(r.login));
            return (
              <tr key={r.login} style={styles.tr}>
                <td style={styles.td}>
                  <span style={{ ...styles.accountId, cursor: 'pointer', textDecoration: 'underline' }} onClick={() => window.location.href = `/pattern/account/${r.login}`}>{r.login}</span>
                  {isRegistered && <span style={styles.registeredBadge}>등록됨</span>}
                </td>
                <td style={styles.td}>{r.trade_count}건</td>
                <td style={styles.td}>
                  <span style={{ color: r.win_rate > 70 ? '#ff6b6b' : r.win_rate > 50 ? '#f7971e' : '#52c41a', fontWeight: 600 }}>
                    {r.win_rate}%
                  </span>
                </td>
                <td style={styles.td}>
                  <span style={{ color: r.avg_profit > 0 ? '#52c41a' : '#ff6b6b' }}>
                    {formatMoney(r.avg_profit)}
                  </span>
                </td>
                <td style={styles.td}>
                  <span style={{ color: r.total_profit > 0 ? '#52c41a' : '#ff6b6b', fontWeight: 700 }}>
                    {formatMoney(r.total_profit)}
                  </span>
                </td>
                <td style={styles.td}>{formatHours(r.avg_hold_hours)}</td>
                <td style={styles.td}><span style={{ color: '#52c41a' }}>{formatMoney(r.avg_win)}</span></td>
                <td style={styles.td}><span style={{ color: '#ff6b6b' }}>{formatMoney(r.avg_loss)}</span></td>
                {showPayoff && <td style={styles.td}>{r.payoff_ratio ?? '-'}</td>}
                <td style={styles.td}>{r.first_trade}</td>
                <td style={styles.td}>{r.last_trade}</td>
                <td style={styles.td}>
                  {!isRegistered ? (
                    <button
                      style={styles.registerBtn}
                      onClick={() => onRegister(r.login)}
                      disabled={registeringLogin === r.login}
                    >
                      {registeringLogin === r.login ? '등록 중...' : 'B북 등록'}
                    </button>
                  ) : (
                    <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px' }}>등록됨</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {data.length === 0 && <div style={styles.empty}>해당 패턴의 계좌가 없습니다.</div>}
    </div>
  );
};

// ── 공통 훅 ────────────────────────────────────────────────
const usePatternPage = (fetcher: () => Promise<any>) => {
  const [data, setData] = useState<TradePattern[]>([]);
  const [loading, setLoading] = useState(true);
  const [registeredAccounts, setRegisteredAccounts] = useState<Set<string>>(new Set());
  const [registeringLogin, setRegisteringLogin] = useState<number | null>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [patternRes, accRes] = await Promise.all([fetcher(), accountAPI.getAll()]);
      setData(patternRes.data);
      setRegisteredAccounts(new Set(accRes.data.map((a: any) => String(a.accountId))));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleRegister = async (login: number) => {
    if (!window.confirm(`계좌 ${login}을 B북으로 등록하시겠습니까?`)) return;
    setRegisteringLogin(login);
    try {
      await accountAPI.register({
        accountId: String(login), isBBook: true, isActive: true,
        thresholdEntry: -5.00, thresholdMin: 0, thresholdMax: -15.00, symbolMode: 'ALL',
      });
      await fetchData();
    } catch (err: any) { alert(err.response?.data?.message || '등록 실패'); }
    finally { setRegisteringLogin(null); }
  };

  return { data, loading, registeredAccounts, registeringLogin, handleRegister, fetchData };
};

// ── 단기 고승률형 ────────────────────────────────────────────
export const ShortHighWinRatePage: React.FC = () => {
  const { data, loading, registeredAccounts, registeringLogin, handleRegister, fetchData } =
    usePatternPage(patternAPI.getShortHighWinRate);
  return (
    <div style={styles.container}>
      <PageHeader
        title="🔴 단기 고승률형"
        subtitle="평균 보유시간 1시간 미만, 승률 80% 이상 — 브로커 최고 위험 계좌"
        color="#ff6b6b"
        count={data.length}
        onRefresh={fetchData}
      />
      <PatternTable data={data} loading={loading} registeredAccounts={registeredAccounts}
        onRegister={handleRegister} registeringLogin={registeringLogin}
        badgeColor="#ff6b6b" badgeLabel="단기 고승률" />
    </div>
  );
};

// ── 장기 안정수익형 ─────────────────────────────────────────
export const LongStablePage: React.FC = () => {
  const { data, loading, registeredAccounts, registeringLogin, handleRegister, fetchData } =
    usePatternPage(patternAPI.getLongStable);
  return (
    <div style={styles.container}>
      <PageHeader
        title="🔴 장기 안정수익형"
        subtitle="평균 보유시간 20시간 이상, 승률 70% 이상 — 전문 트레이더 위험 계좌"
        color="#ff7a45"
        count={data.length}
        onRefresh={fetchData}
      />
      <PatternTable data={data} loading={loading} registeredAccounts={registeredAccounts}
        onRegister={handleRegister} registeringLogin={registeringLogin}
        badgeColor="#ff7a45" badgeLabel="장기 안정" />
    </div>
  );
};

// ── 고Payoff형 ──────────────────────────────────────────────
export const HighPayoffPage: React.FC = () => {
  const { data, loading, registeredAccounts, registeringLogin, handleRegister, fetchData } =
    usePatternPage(patternAPI.getHighPayoff);
  return (
    <div style={styles.container}>
      <PageHeader
        title="🟡 고Payoff형"
        subtitle="승률 50% 미만이지만 수익 중 — 손실은 작고 수익은 큰 역추세 트레이더"
        color="#f7971e"
        count={data.length}
        onRefresh={fetchData}
      />
      <PatternTable data={data} loading={loading} showPayoff registeredAccounts={registeredAccounts}
        onRegister={handleRegister} registeringLogin={registeringLogin}
        badgeColor="#f7971e" badgeLabel="고Payoff" />
    </div>
  );
};

// ── 일반 손실형 ─────────────────────────────────────────────
export const NormalLossPage: React.FC = () => {
  const { data, loading, registeredAccounts, registeringLogin, handleRegister, fetchData } =
    usePatternPage(patternAPI.getNormalLoss);
  return (
    <div style={styles.container}>
      <PageHeader
        title="🟢 일반 손실형"
        subtitle="승률 50% 미만, 총손익 마이너스 — 브로커에게 유리한 일반 리테일 계좌"
        color="#52c41a"
        count={data.length}
        onRefresh={fetchData}
      />
      <PatternTable data={data} loading={loading} registeredAccounts={registeredAccounts}
        onRegister={handleRegister} registeringLogin={registeringLogin}
        badgeColor="#52c41a" badgeLabel="일반 손실" />
    </div>
  );
};

// ── 기존 패턴 분석 (Monitor DB 추천) ───────────────────────
type Recommendation = {
  login: number;
  bbookRiskScore: number;
  lossprobScore: number;
  finalScore: number;
  recommendedBook: string;
  confidence: string;
  updatedAt: string;
};

type AccountDetail = {
  recommendation?: Recommendation;
  risk?: any;
  lossprob?: any;
};

const ScoreBar: React.FC<{ value: number; color?: string }> = ({ value, color = '#667eea' }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <div style={{ flex: 1, height: '6px', background: 'rgba(255,255,255,0.1)', borderRadius: '3px', overflow: 'hidden' }}>
      <div style={{ width: `${Math.min(value, 100)}%`, height: '100%', background: color, borderRadius: '3px' }} />
    </div>
    <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', minWidth: '36px', textAlign: 'right' }}>{value?.toFixed(1)}</span>
  </div>
);

export const RecommendationPage: React.FC = () => {
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'ALL' | 'A' | 'B'>('ALL');
  const [search, setSearch] = useState('');
  const [selectedLogin, setSelectedLogin] = useState<number | null>(null);
  const [detail, setDetail] = useState<AccountDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [registeredAccounts, setRegisteredAccounts] = useState<Set<string>>(new Set());
  const [registeringLogin, setRegisteringLogin] = useState<number | null>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [recoRes, accRes] = await Promise.all([patternAPI.getRecommendations(), accountAPI.getAll()]);
      setRecommendations(recoRes.data);
      setRegisteredAccounts(new Set(accRes.data.map((a: any) => String(a.accountId))));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSelect = async (login: number) => {
    if (selectedLogin === login) { setSelectedLogin(null); setDetail(null); return; }
    setSelectedLogin(login);
    setDetailLoading(true);
    try {
      const res = await patternAPI.getAccountPattern(String(login));
      setDetail(res.data);
    } catch { } finally { setDetailLoading(false); }
  };

  const handleRegister = async (login: number) => {
    if (!window.confirm(`계좌 ${login}을 B북으로 등록하시겠습니까?`)) return;
    setRegisteringLogin(login);
    try {
      await accountAPI.register({ accountId: String(login), isBBook: true, isActive: true, thresholdEntry: -5.00, thresholdMin: 0, thresholdMax: -15.00, symbolMode: 'ALL' });
      await fetchData();
    } catch (err: any) { alert(err.response?.data?.message || '등록 실패'); }
    finally { setRegisteringLogin(null); }
  };

  const filtered = recommendations.filter(r =>
    (filter === 'ALL' || r.recommendedBook === filter) && String(r.login).includes(search)
  );

  const scoreColor = (s: number) => s >= 70 ? '#ff6b6b' : s >= 40 ? '#f7971e' : '#52c41a';

  return (
    <div style={styles.container}>
      <PageHeader title="📊 Monitor DB 추천 분석" subtitle="외부 분석 엔진 기반 A/B북 추천 (참고용)" color="#667eea" count={recommendations.length} onRefresh={fetchData} />
      <div style={styles.toolbar}>
        <input style={styles.search} placeholder="🔍 계좌번호 검색" value={search} onChange={e => setSearch(e.target.value)} />
        <div style={styles.filterGroup}>
          {(['ALL', 'A', 'B'] as const).map(f => (
            <button key={f} style={filter === f ? { ...styles.filterBtn, ...styles.filterBtnActive } : styles.filterBtn} onClick={() => setFilter(f)}>
              {f === 'ALL' ? '전체' : f === 'A' ? '🔴 A북' : '🟢 B북'}
            </button>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={styles.tableWrapper}>
            {loading ? <div style={styles.loading}>로딩 중...</div> : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>계좌번호</th>
                    <th style={styles.th}>추천</th>
                    <th style={styles.th}>Final Score</th>
                    <th style={styles.th}>B북 리스크</th>
                    <th style={styles.th}>손실확률</th>
                    <th style={styles.th}>신뢰도</th>
                    <th style={styles.th}>액션</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(r => {
                    const isRegistered = registeredAccounts.has(String(r.login));
                    return (
                      <tr key={r.login} style={{ ...styles.tr, background: selectedLogin === r.login ? 'rgba(102,126,234,0.1)' : 'transparent', cursor: 'pointer' }} onClick={() => handleSelect(r.login)}>
                        <td style={styles.td}>
                          <span style={{ ...styles.accountId, cursor: 'pointer', textDecoration: 'underline' }} onClick={() => window.location.href = `/pattern/account/${r.login}`}>{r.login}</span>
                          {isRegistered && <span style={styles.registeredBadge}>등록됨</span>}
                        </td>
                        <td style={styles.td}>
                          <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, background: r.recommendedBook === 'B' ? 'rgba(0,210,255,0.15)' : 'rgba(255,107,107,0.15)', color: r.recommendedBook === 'B' ? '#00d2ff' : '#ff6b6b' }}>
                            {r.recommendedBook === 'B' ? '🟢 B북' : '🔴 A북'}
                          </span>
                        </td>
                        <td style={styles.td}><span style={{ color: scoreColor(r.finalScore), fontWeight: 700 }}>{r.finalScore?.toFixed(1)}</span></td>
                        <td style={styles.td}>{r.bbookRiskScore?.toFixed(1)}</td>
                        <td style={styles.td}>{r.lossprobScore?.toFixed(1)}</td>
                        <td style={styles.td}>
                          <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 700, background: r.confidence === 'HIGH' ? 'rgba(82,196,26,0.2)' : r.confidence === 'MEDIUM' ? 'rgba(247,151,30,0.2)' : 'rgba(255,107,107,0.2)', color: r.confidence === 'HIGH' ? '#52c41a' : r.confidence === 'MEDIUM' ? '#f7971e' : '#ff6b6b' }}>
                            {r.confidence}
                          </span>
                        </td>
                        <td style={styles.td}>
                          {!isRegistered && r.recommendedBook === 'B' && (
                            <button style={styles.registerBtn} onClick={e => { e.stopPropagation(); handleRegister(r.login); }} disabled={registeringLogin === r.login}>
                              {registeringLogin === r.login ? '등록 중...' : 'B북 등록'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {!loading && filtered.length === 0 && <div style={styles.empty}>조건에 맞는 계좌가 없습니다.</div>}
          </div>
        </div>
        {selectedLogin && (
          <div style={styles.detailPanel}>
            <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#fff', margin: '0 0 16px' }}>계좌 {selectedLogin} 상세</h3>
            {detailLoading ? <div style={styles.loading}>로딩 중...</div> : detail && (
              <>
                {detail.recommendation && (
                  <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginBottom: '12px' }}>📊 추천 요약</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>Final Score</span>
                      <span style={{ color: scoreColor(detail.recommendation.finalScore), fontWeight: 700, fontSize: '18px' }}>{detail.recommendation.finalScore?.toFixed(1)}</span>
                    </div>
                  </div>
                )}
                {detail.risk && (
                  <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginBottom: '12px' }}>⚠️ B북 리스크</div>
                    {[['전체 리스크', detail.risk.scoreBbookRisk, '#ff6b6b'], ['거래 빈도', detail.risk.scoreFrequency, '#f7971e'], ['변동성', detail.risk.scoreVolatility, '#667eea']].map(([label, val, color]) => (
                      <div key={label as string} style={{ marginBottom: '10px' }}>
                        <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>{label as string}</span>
                        <ScoreBar value={val as number} color={color as string} />
                      </div>
                    ))}
                  </div>
                )}
                {detail.lossprob && (
                  <div>
                    <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginBottom: '12px' }}>📉 손실 확률</div>
                    {[['거래 수', `${detail.lossprob.trades}건`], ['승률', `${(detail.lossprob.winRate * 100).toFixed(1)}%`], ['기대값', formatMoney(detail.lossprob.expectancy)]].map(([label, val]) => (
                      <div key={label as string} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>{label as string}</span>
                        <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>{val as string}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── 공통 헤더 컴포넌트 ─────────────────────────────────────
const PageHeader: React.FC<{ title: string; subtitle: string; color: string; count: number; onRefresh: () => void }> = ({ title, subtitle, color, count, onRefresh }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
    <div>
      <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#fff', margin: '0 0 4px' }}>
        {title} <span style={{ fontSize: '16px', color, marginLeft: '8px' }}>{count}개</span>
      </h1>
      <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', margin: 0 }}>{subtitle}</p>
    </div>
    <button style={styles.refreshBtn} onClick={onRefresh}>🔄 새로고침</button>
  </div>
);

// ── 스타일 ─────────────────────────────────────────────────
const styles: Record<string, React.CSSProperties> = {
  container: { color: '#fff', maxWidth: '100%' },
  toolbar: { display: 'flex', gap: '12px', marginBottom: '20px', alignItems: 'center' },
  search: { flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '10px', padding: '10px 16px', color: '#fff', fontSize: '14px', outline: 'none' },
  filterGroup: { display: 'flex', gap: '8px' },
  filterBtn: { padding: '8px 16px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'rgba(255,255,255,0.5)', fontSize: '13px', cursor: 'pointer' },
  filterBtnActive: { background: 'rgba(102,126,234,0.2)', borderColor: '#667eea', color: '#667eea' },
  tableWrapper: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '900px' },
  th: { padding: '12px 16px', textAlign: 'left', color: 'rgba(255,255,255,0.4)', fontSize: '12px', fontWeight: 600, borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid rgba(255,255,255,0.05)' },
  td: { padding: '12px 16px', color: 'rgba(255,255,255,0.8)', fontSize: '14px', whiteSpace: 'nowrap' },
  accountId: { fontFamily: 'monospace', background: 'rgba(102,126,234,0.15)', padding: '2px 8px', borderRadius: '4px', color: '#667eea' },
  registeredBadge: { marginLeft: '6px', padding: '1px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 700, background: 'rgba(82,196,26,0.2)', color: '#52c41a' },
  registerBtn: { background: 'rgba(0,210,255,0.15)', border: '1px solid rgba(0,210,255,0.3)', borderRadius: '6px', padding: '4px 10px', color: '#00d2ff', fontSize: '12px', cursor: 'pointer' },
  refreshBtn: { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '8px 16px', color: 'rgba(255,255,255,0.7)', fontSize: '13px', cursor: 'pointer' },
  loading: { padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.4)' },
  empty: { padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '14px' },
  detailPanel: { width: '280px', flexShrink: 0, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '20px', position: 'sticky', top: '20px' },
};

export default RecommendationPage;
